import { ApplicationRef, Injectable, Injector } from '@angular/core';
import { Colors, StopMarkerStatus } from 'src/app/models/enums';
import { MarkerStopComponent } from 'src/app/components/markers';
import { createComponent } from '@angular/core';
import { images } from 'src/images';

@Injectable({
  providedIn: 'root'
})
export class MarkerRenderService {

  private markerElementCache = new Map<string, HTMLElement>();
  private advancedMarkerElementClass: typeof google.maps.marker.AdvancedMarkerElement | null = null;

  constructor(
    private applicationRef: ApplicationRef,
    private injector: Injector
  ) {}

  async getAdvancedMarkerElementClass(): Promise<typeof google.maps.marker.AdvancedMarkerElement> {
    if (!this.advancedMarkerElementClass) {
      const { AdvancedMarkerElement } = (await google.maps.importLibrary('marker')) as google.maps.MarkerLibrary;
      this.advancedMarkerElementClass = AdvancedMarkerElement;
    }
    return this.advancedMarkerElementClass;
  }

  getMarkerElement(status: string, color?: string): HTMLElement {
    const cacheKey = `${status}:${color ?? ''}`;
    const cached = this.markerElementCache.get(cacheKey);
    if (cached) return cached.cloneNode(true) as HTMLElement;

    let element: HTMLElement;
    if (status in images.routes.stops) {
      element = this.getMarkerIcon(images.routes.stops[status as keyof typeof images.routes.stops]);
    } else {
      const componentRef = createComponent(MarkerStopComponent, {
        environmentInjector: this.applicationRef.injector,
        elementInjector: this.injector
      });
      const colorMap: Record<string, string> = {
        [StopMarkerStatus.SELECTED]: '#8136db',
        [StopMarkerStatus.HOVER]: '#BD95EC',
      };
      let markerColor = '#280a54';
      if (color || status) {
        markerColor = color ?? colorMap[status];
      }
      componentRef.setInput('color', markerColor);
      componentRef.changeDetectorRef.detectChanges();
      element = componentRef.location.nativeElement;
    }

    this.markerElementCache.set(cacheKey, element);
    return element.cloneNode(true) as HTMLElement;
  }

  getStopCircle(stop: any, color?: string): google.maps.Circle | null {
    const radius = Number(stop.radius);
    if (!radius || radius <= 0) return null;
    return new google.maps.Circle({
      center: { lat: stop.lat, lng: stop.lng },
      radius,
      fillColor: color || Colors.PURPLE,
      fillOpacity: 0.2,
      strokeWeight: 0,
      clickable: false,
      zIndex: 1
    });
  }

  private getMarkerIcon(icon: string): HTMLElement {
    // The icon needs a real box in normal flow: an absolutely-positioned img collapses the
    // marker content to 0x0, leaving the AdvancedMarkerElement without a click hit region.
    // The wrapper is anchored bottom-center on the position, so half its height shifts it
    // down to keep the icon visually centered on the stop coordinates.
    const iconWrapper = document.createElement('div');
    iconWrapper.style.width = '24px';
    iconWrapper.style.height = '24px';
    iconWrapper.style.transform = 'translateY(12px)';
    const iconImg = document.createElement('img');
    iconImg.src = icon;
    iconImg.style.display = 'block';
    iconImg.style.width = '100%';
    iconImg.style.height = '100%';
    if (icon === images.routes.stops.demandDefault) {
      iconImg.style.border = `solid 2px #280a54`;
      iconImg.style.borderRadius = '50%';
    }
    iconWrapper.appendChild(iconImg);
    return iconWrapper;
  }
}
